import React, { useCallback, useEffect, useRef } from 'react'
import USBInput from '@usb-shield/react-input'
import { PropTypes } from 'prop-types'
import { isNullOrEmpty,isLoginEnabled } from '../../../utils/Utility'

/**
 * Returns customized USBInput
 * @param {FieldsSchema[x]} fieldSchema - Any object, same shape as UserManagerFieldsArray (an item from array. We are dealing with only one field from array)
 * @param {string} fieldLabel - case where field label should be different from fieldSchema label
 * @param {bool} isReadOnly - marks field as readOnly - default false
 * @param {umFieldsState} fieldState - Any object, same shape as useUserManagerFieldsHook initialState
 * @param {} setFieldState - function to set the (fieldState) state.
 * @param {} onBlur - optional OnBlur event.
 * @param {} onChange - optional onChange event.
 * @param {} onKeyPress - optional onKeyPress event.
 */
const ShieldInput = ({
  fieldSchema,
  fieldLabel,
  isReadOnly = false,
  fieldState,
  setFieldState,
  onSensitiveValueChange,
  onBlur,
  onChange,
  onKeyPress,
  resetCounter,
  addClasses
}) => {
  const inputRef = useRef(null)
  const isSensitivePasswordField = fieldSchema && fieldSchema.type === 'password' && onSensitiveValueChange

  const getInputElement = useCallback(
    () => (inputRef.current ? inputRef.current.getElementsByTagName('input')[0] : null),
    []
  )

  /**
   * Adding useEffect() to focus on CompanyID field on the load of the Login Page.
   */
  useEffect(() => {
    if (inputRef && inputRef.current !== null) {
      const input = inputRef.current.getElementsByTagName('input')[0]
      if(fieldSchema.id === 'companyId' && !fieldSchema.isReadOnly && isLoginEnabled())
        input.focus()
    }
  }, [])

  /**
   * Adds value as title to the input textbox, so complete text will be visible on hover
   */
  useEffect(() => {
    if (inputRef && inputRef.current !== null) {
      const input = inputRef.current.getElementsByTagName('input')[0]
      input.title = isSensitivePasswordField ? '' : fieldState && fieldState.value
    }
  }, [fieldState && fieldState.value, isSensitivePasswordField])

  useEffect(() => {
    if (isSensitivePasswordField) {
      const input = getInputElement()

      if (input) {
        input.value = ''
      }
    }
  }, [isSensitivePasswordField, resetCounter, getInputElement])

  /**
   * setFieldState updates the Fields state for the given property (eg: isFocused) and preserves the state of other fields
   */
  const onFocusHandler = () => {
    setFieldState(fieldSchema.id, { isFocused: true })
  }

  const onBlurHandler = e => {
    setFieldState(fieldSchema.id, { isFocused: false })
    // when fieldState has any other errors, make them false
    if (Object.prototype.hasOwnProperty.call(fieldState, 'isPasswordMismatch') && e.target.value === '')
      setFieldState(fieldSchema.id, { changed: false, isPasswordMismatch: false, isFocused: false })

    if (onBlur) {
      // raise event which was supplied in props
      onBlur(e)
    }
  }

  /**
   * Updates the state to, value from input textbox
   */
  const onChangeHandler = e => {
    if (isSensitivePasswordField) {
      setFieldState(fieldSchema.id, {
        changed: e.target.value.trim().length > 0,
        isError: false,
        isPasswordMismatch: false,
        isUserExistsError: false
      })
      onSensitiveValueChange(fieldSchema.id, e.target.value)
    } else {
      setFieldState(fieldSchema.id, { value: e.target.value })
    }
    if (onChange) {
      // raise event given from props
      onChange(e)
    }
  }

  /**
   * We want to show the helper text when the field is active
   * When field is marked as not required and has error text present, then return error text, else return Optional
   * When field is readOnly then return empty string
   */
  const getHelperText = () => {
    if (fieldState.isFocused)
      return isReadOnly || fieldSchema.errorText === 'Read Only'
        ? 'Read Only'
        : fieldSchema.errorText
    if (fieldSchema.isReadOnly) return ''
    if (!fieldSchema.isRequired) return 'Optional'
    // if (fieldSchema.isRequired && isReadOnly) return 'Read Only'
    return '  '
  }

  /**
   * When field typ is password, we should pass these props so, show/hide button will show up
   */
  let showHide = {}
  if (fieldSchema && fieldSchema.type === 'password')
    showHide = {
      isShowHide: true,
      showBtnLabel: 'show',
      hideBtnLabel: 'hide'
    }

  /**
   * Returns error text
   */
  const getErrorText = () => {
    let { errorText } = fieldSchema
    if (fieldState && fieldState.isUserExistsError) errorText = fieldSchema.userExistError
    else if (fieldState && fieldState.isPasswordMismatch) errorText = fieldSchema.pwdMismatchErrorText
    else if (fieldState && fieldState.isCompanyExistError) errorText = fieldSchema.companyExistError
    else if (fieldState && fieldState.isAlternateIdExistError) errorText = fieldSchema.alternateIdExistError

    return errorText
  }

  // Move ariaReadOnly to render scope so it is always up to date
  const ariaReadOnly = isReadOnly ? { readOnly: 'true' } : {}
  const inputValueProps = isSensitivePasswordField ? { defaultValue: '' } : { value: fieldState.value }

  return (
    <>
      <USBInput
        forwardedRef={inputRef}
        inputId={fieldSchema.id}
        name={fieldSchema.id}
        type="text"
        maxLength={fieldSchema.maxLength}
        labelText={isNullOrEmpty(fieldLabel) !== '' ? fieldLabel : fieldSchema.label}
        errorText={getErrorText()}
        isError={fieldState.isError}
        handleFocus={() => onFocusHandler()}
        handleBlur={e => onBlurHandler(e)}
        handleChange={e => onChangeHandler(e)}
        helperText={getHelperText()}
        ariaProps={ariaReadOnly}
        isReadOnly={isReadOnly}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...inputValueProps}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...showHide}
        handleKeyPress={onKeyPress != null || onKeyPress !== undefined ? onKeyPress : null}
        // isHideHelperTextOnError
        addClasses={`input-border ${addClasses}`}
      />
    </>
  )
}

ShieldInput.propTypes = {
  fieldSchema: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    minLength: PropTypes.number.isRequired,
    maxLength: PropTypes.number.isRequired,
    pattern: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(RegExp)]),
    errorText: PropTypes.string.isRequired,
    userExistError: PropTypes.string,
    pwdMismatchErrorText: PropTypes.string,
    isRequired: PropTypes.bool.isRequired,
    isReadOnly: PropTypes.bool,
    companyExistError: PropTypes.string,
    alternateIdExistError: PropTypes.string
  }).isRequired,

  isReadOnly: PropTypes.bool,

  fieldState: PropTypes.shape({
    id: PropTypes.string.isRequired,
    value: PropTypes.string, // not required
    isError: PropTypes.bool.isRequired,
    isUserExistsError: PropTypes.bool,
    isFocused: PropTypes.bool.isRequired,
    isPasswordMismatch: PropTypes.bool,
    isCompanyExistError: PropTypes.bool,
    isAlternateIdExistError: PropTypes.bool
  }).isRequired,

  setFieldState: PropTypes.func,
  onSensitiveValueChange: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onKeyPress: PropTypes.func,
  fieldLabel: PropTypes.string,
  resetCounter: PropTypes.number,
  addClasses: PropTypes.string
}

ShieldInput.defaultProps = {
  onBlur: null,
  onChange: null,
  onKeyPress: null,
  onSensitiveValueChange: null,
  isReadOnly: false,
  resetCounter: 0,
  setFieldState: null,
  fieldLabel: '',
  addClasses: ''
}

export default ShieldInput
